import { useQuery, useSuspenseQuery } from '@apollo/client';

import { type PatientImportJobStatus } from '@eluve/api-contract';
import { eluveAdminHasuraContext } from '@eluve/apollo-client';

import { getPatientImportJobQuery } from '../patient-import.operations';

export type ImportJob = {
  id: string;
  tenantId: string;
  patientImportSourceId: string;
  userUploadId: string;
  filename: string;
  status: PatientImportJobStatus;
  totalRows: number | null;
  unknownHeaders: string[] | null;
  missingRequiredHeaders: string[] | null;
  committedCount: number;
  failedCount: number;
  skippedCount: number;
  failureCategory: string | null;
  failureMessage: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  finalizedAt: string | null;
  cancelledAt: string | null;
};

type ImportJobQueryData = {
  patientImportJobsByPk?: ImportJob | null;
};

const TERMINAL_STATUSES = new Set<PatientImportJobStatus>([
  'COMPLETED',
  'PARTIALLY_COMPLETED',
  'CANCELLED',
  'FAILED',
  'HEADER_MISMATCH',
]);

const POLL_INTERVAL_MS = 2000;

export const useImportJob = (tenantId: string, jobId: string): ImportJob => {
  const { data } = useSuspenseQuery<ImportJobQueryData>(
    getPatientImportJobQuery,
    {
      variables: { tenantId, jobId },
      context: eluveAdminHasuraContext,
    },
  );

  const raw = data.patientImportJobsByPk;
  const status = raw?.status as PatientImportJobStatus | undefined;
  const isTerminal = status === undefined || TERMINAL_STATUSES.has(status);

  useQuery<ImportJobQueryData>(getPatientImportJobQuery, {
    variables: { tenantId, jobId },
    context: eluveAdminHasuraContext,
    pollInterval: isTerminal ? 0 : POLL_INTERVAL_MS,
    fetchPolicy: 'cache-and-network',
    skip: isTerminal,
  });

  if (!raw || !status) {
    throw new Error(`Patient import job ${jobId} not found`);
  }

  return {
    ...raw,
    patientImportSourceId: String(raw.patientImportSourceId),
    status,
    totalRows: raw.totalRows ?? null,
    unknownHeaders: (raw.unknownHeaders as string[] | null) ?? null,
    missingRequiredHeaders:
      (raw.missingRequiredHeaders as string[] | null) ?? null,
    failureCategory: raw.failureCategory ?? null,
    failureMessage: raw.failureMessage ?? null,
    finalizedAt: raw.finalizedAt ?? null,
    cancelledAt: raw.cancelledAt ?? null,
  };
};
